import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { HiOutlineCheck, HiOutlineChevronDown, HiOutlineUsers } from "react-icons/hi2";
import { useSelector } from "react-redux";
import TimesheetSearchField from "./TimesheetSearchField";
import { getParticipantLabel } from "Constants/constant";
import { getSpRosteringSetting, getSpSetting } from "store/selector/sp.selector";
import {
  buildAllParticipantsOption,
  isAllParticipantsSelection,
  isParticipantOptionSelected,
  orderTimesheetParticipantOptions,
  toggleParticipantSelection,
} from "../../utils/timesheetParticipantFilter";

const TRIGGER_CLASS =
  "flex h-[34px] w-full min-w-0 items-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-[#F8FAFC] px-[11px] box-border text-left transition-colors hover:border-[#C5D4EA] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6BB6]/30 md:w-auto md:min-w-[200px] md:max-w-[260px]";

const TimesheetParticipantSelect = ({
  options = [],
  selectedOptions = [],
  onChange,
  placeholder,
}) => {
  const spRosteringSetting = useSelector(getSpRosteringSetting);
  const spSetting = useSelector(getSpSetting);
  const careSector = spRosteringSetting?.care_sector ?? spSetting?.care_sector;
  const participantLabelPlural = getParticipantLabel(careSector, true);
  const allOption = useMemo(
    () => buildAllParticipantsOption(participantLabelPlural),
    [participantLabelPlural]
  );
  const allLabel = allOption.label;
  const resolvedPlaceholder = placeholder ?? allLabel;
  const searchPlaceholder = `Search ${participantLabelPlural.toLowerCase()}...`;
  const emptyMessage = `No ${participantLabelPlural.toLowerCase()} found`;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const listScrollRef = useRef(0);
  const holdListScrollRef = useRef(false);

  const selection = Array.isArray(selectedOptions) ? selectedOptions : [];
  const isAll = isAllParticipantsSelection(selection);
  const selectedCount = isAll
    ? 0
    : selection.filter((opt) => String(opt?.value) !== "all").length;

  const displayLabel = useMemo(() => {
    if (isAll || selectedCount === 0) return resolvedPlaceholder;
    if (selectedCount === 1) {
      return selection[0]?.label || resolvedPlaceholder;
    }
    const first = selection[0]?.label || "Selected";
    return `${first} +${selectedCount - 1}`;
  }, [isAll, resolvedPlaceholder, selectedCount, selection]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q
      ? options
      : options.filter((opt) =>
          String(opt.label || "")
            .toLowerCase()
            .includes(q)
        );
    return orderTimesheetParticipantOptions(base, selection);
  }, [options, search, selection]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useLayoutEffect(() => {
    if (!holdListScrollRef.current || !listRef.current) return;
    const top = listScrollRef.current;
    const list = listRef.current;
    list.scrollTop = top;
    const frame = requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = top;
      holdListScrollRef.current = false;
    });
    return () => cancelAnimationFrame(frame);
  });

  const handleToggle = (option) => {
    if (String(option?.value) !== "all" && listRef.current) {
      listScrollRef.current = listRef.current.scrollTop;
      holdListScrollRef.current = true;
    }
    const next = toggleParticipantSelection(selection, option, allOption);
    onChange?.(next);
    if (String(option?.value) === "all") {
      setOpen(false);
      setSearch("");
    }
  };

  return (
    <div ref={rootRef} className="relative w-full min-w-0 md:w-auto md:shrink-0">
      <button
        type="button"
        className={TRIGGER_CLASS}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <HiOutlineUsers
          className="h-4 w-4 shrink-0 text-[#64748B]"
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-none text-[#0F172A] capitalize">
          {displayLabel}
        </span>
        {!isAll && selectedCount > 1 ? (
          <span className="shrink-0 rounded-md bg-[#EEF2FB] px-1.5 py-0.5 text-[10px] font-bold text-[#4A6BB6]">
            {selectedCount}
          </span>
        ) : null}
        <HiOutlineChevronDown
          className={`h-4 w-4 shrink-0 text-[#94A3B8] transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          className="absolute left-0 top-[calc(100%+6px)] z-[10020] w-[min(300px,calc(100vw-32px))] overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          role="listbox"
          aria-multiselectable="true"
        >
          <div className="border-b border-[#E5E7EB] p-3">
            <TimesheetSearchField
              value={search}
              onChange={setSearch}
              placeholder={searchPlaceholder}
              autoFocus
            />
          </div>
          <ul
            ref={listRef}
            onScroll={() => {
              if (holdListScrollRef.current || !listRef.current) return;
              listScrollRef.current = listRef.current.scrollTop;
            }}
            style={{ overflowAnchor: "none" }}
            className="timesheet-panel-scroll max-h-[250px] overflow-y-auto py-1"
          >
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2.5 text-[13px] text-[#94A3B8]">
                {emptyMessage}
              </li>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = isParticipantOptionSelected(selection, opt);
                const next = filteredOptions[index + 1];
                const dividesPinnedGroup =
                  isSelected &&
                  String(opt?.value) !== "all" &&
                  Boolean(next) &&
                  !isParticipantOptionSelected(selection, next);
                return (
                  <li
                    key={String(opt.value)}
                    className={
                      dividesPinnedGroup ? "border-b border-[#E5E7EB]" : undefined
                    }
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] capitalize transition-colors ${
                        isSelected
                          ? "bg-[#EEF2FB] font-semibold text-[#4A6BB6]"
                          : "font-medium text-[#0F172A] hover:bg-[#F8FAFC]"
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleToggle(opt)}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          isSelected
                            ? "border-[#4A6BB6] bg-[#4A6BB6] text-white"
                            : "border-[#CBD5E1] bg-white"
                        }`}
                        aria-hidden
                      >
                        {isSelected ? (
                          <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
                        ) : null}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TimesheetParticipantSelect;
